import { escapeHtml } from '../format';
import { copyText } from './dom';
import type { UiEnvironment } from './environment';
import type { Toast } from './toast';

/**
 * KakaoTalk's in-app browser can block file pickers and image downloads
 * (spec §6.1). Same guidance pattern as the timeline tool in src/main.ts.
 */

function browserName(environment: UiEnvironment): string {
  if (environment.kakaoInAppIos) return 'Safari';
  return environment.mobile ? 'Chrome이나 삼성 인터넷' : '기본 브라우저';
}

export function inAppNoticeMarkup(environment: UiEnvironment, context: 'start' | 'save'): string {
  if (!environment.kakaoInApp) return '';
  const browser = browserName(environment);
  const title = context === 'start'
    ? '카카오톡 안에서는 파일 고르기가 막힐 수 있어요'
    : '저장이 안 되면 다른 브라우저에서 열어 주세요';
  const description = context === 'start'
    ? `‘메일로 보내기’는 여기서도 잘 돼요. 파일로 올리려면 ${browser}에서 열어 주세요.`
    : `결과는 서버에 저장하지 않아서 다른 브라우저로 옮겨 갈 수 없어요. ${browser}에서 다시 분석하면 저장이 잘 돼요.`;
  const open = environment.externalBrowserUrl
    ? `<a class="button button-kakao-browser" href="${escapeHtml(environment.externalBrowserUrl)}">기본 브라우저로 열기</a>`
    : '';
  return `
    <aside class="krc-inapp" role="note" aria-label="카카오톡 브라우저 안내">
      <span class="krc-inapp-badge">카카오톡 브라우저</span>
      <strong>${title}</strong>
      <p>${escapeHtml(description)}</p>
      <div class="krc-inapp-actions">
        ${open}
        <button class="button ${open ? 'button-soft' : 'button-kakao-browser'}" type="button" data-krc-copy-page>주소 복사</button>
      </div>
      <small>또는 카카오톡의 더보기(⋮) 메뉴에서 ‘다른 브라우저로 열기’를 선택하세요.</small>
    </aside>`;
}

export function bindInAppNotice(root: ParentNode, environment: UiEnvironment, toast: Toast): void {
  root.querySelectorAll<HTMLButtonElement>('[data-krc-copy-page]').forEach((button) => {
    button.addEventListener('click', async () => {
      const copied = await copyText(environment.pageUrl);
      const browser = browserName(environment);
      toast(copied ? `주소를 복사했어요. ${browser} 주소창에 붙여넣어 주세요.` : '주소를 복사하지 못했어요. 더보기 메뉴에서 다른 브라우저로 열어 주세요.');
    });
  });
}
